import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, HostListener, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, UrlSerializer } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthContext } from '@wb/core/auth.context';
import dayjs from 'dayjs';
import { Subject, combineLatest } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { AccessType } from './core/enums/access-type';
import { Account } from './core/interfaces/account';
import { AccountPermission, PermissionAndAccountUid, PermissionList } from './core/interfaces/account-permission';
import { Admin, SidebarAnnouncement } from './core/interfaces/admin';
import { AccountService } from './core/services/account.service';
import { AnalyticService } from './core/services/analytic.service';
import { AuthService } from './core/services/auth.service';
import { DisplayOrientation, DisplayService } from './core/services/display.service';
import { FeatureService } from './core/services/feature.service';
import { MessageService } from './core/services/message.service';
import { RoutingService } from './core/services/routing.service';
import { UserService } from './core/services/user.service';
import { WidgetService } from './core/services/widget.service';
import { TermsComponent } from './shared/components/legal/terms/terms.component';
import { PinDialogComponent } from './shared/components/pin-dialog/pin-dialog.component';
import { SubMenuService } from './shared/menus/sub-menu';
import { AnnouncementType } from '@wb/core/enums/settings';
import { getAnnouncementLogo } from '@wb/shared/utils/helpers';

interface RouteDataInterface {
  submenu?: boolean;
  route?: string;
}

interface SupportDetails {
  uid?: string;
  account?: string;
  url?: string;
  marketScanId?: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  isFixedInViewport: boolean;

  fixedTopGap: number;

  fixedBottomGap: number;

  currentWidth!: number;

  isMainMenuOpen: boolean = false;

  isSubMenuOpen: boolean;

  routeData!: RouteDataInterface;

  firstName: string | undefined;

  lastName: string | undefined;

  image: string | undefined;

  title: string | undefined;

  submenuTitle: string | undefined;

  baseUrlPath: string | undefined;

  externalRole: boolean = false;

  permission: PermissionList | undefined;

  destroySideNav: boolean = false;

  hideAnnouncement: boolean = false;

  announcementType: AnnouncementType = AnnouncementType.PODIUM; // Current announcement type

  // Set up the announcement banner based on the announcement type
  announcementData: SidebarAnnouncement = {
    logo: getAnnouncementLogo(this.announcementType),
    header: `announcements.${this.announcementType}.header`,
    body: this.translate.instant(`announcements.${this.announcementType}.body`),
    button: this.translate.instant(`announcements.${this.announcementType}.button`),
  };

  hideNotification: boolean = false;

  notificationMessage: string | undefined;

  selectedAccountData: SupportDetails = {};

  selectedAccountName: string | undefined;

  loading: boolean = false;

  loadingMessage: string | undefined;

  fallbackView: string[] = [];

  private readonly timeoutLength: number = 60; // user timeout in minutes

  private userRoles: AccountPermission[] = [];

  private destroyed$: Subject<void> = new Subject<void>();

  private messageListener?: (event: MessageEvent<any>) => any;

  private lastCurrentUserInteraction = dayjs();

  private timeoutTimer?: ReturnType<typeof setTimeout>;

  private timeoutSnackBarRef?: MatSnackBarRef<any>;

  private isSubmenu: boolean = false;

  private userDefaultAccount: string | undefined;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private readonly router: Router,
    private readonly routing: RoutingService,
    private readonly route: ActivatedRoute,
    private readonly renderer: Renderer2,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    private readonly translate: TranslateService,
    private readonly authContext: AuthContext,
    private readonly snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
    private readonly messageService: MessageService,
    private readonly widgetService: WidgetService,
    private readonly analyticService: AnalyticService,
    public readonly userService: UserService,
    public readonly accountService: AccountService,
    public readonly authService: AuthService,
    public readonly subMenuService: SubMenuService,
    public readonly feature: FeatureService,
    public readonly serializer: UrlSerializer,
    public readonly displayService: DisplayService,
  ) {
    this.isFixedInViewport = true;
    this.fixedTopGap = 0;
    this.fixedBottomGap = 0;
    this.isMainMenuOpen = false;
    this.isSubMenuOpen = false;

    // set default language for translation
    this.translate.setDefaultLang('en');
    this.translate.currentLang = 'en';

    // add custom icons
    this.addCustomIcons();

    this.setupNotifications();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (event) {
      this.currentWidth = event.target.innerWidth;
    }
  }

  @HostListener('mousemove', ['$event'])
  onCurrentUserTimeout(event: any) {
    if (event) {
      const compareInteraction = dayjs(this.lastCurrentUserInteraction).add(this.timeoutLength, 'minutes');
      if (compareInteraction > dayjs() && !this.router.url.includes('/auth')) {
        this.timeoutCurrentUserSession();
      }
      this.lastCurrentUserInteraction = dayjs();

      if (this.timeoutSnackBarRef) {
        setTimeout(() => this.timeoutSnackBarRef?.dismiss(), 1000 * 60 * 10); // Display for 10 seconds after the user moves their mouse again
      }
    }
  }

  async ngOnInit(): Promise<void> {
    // If the user is authenticating then show the loading spinner
    this.authService.authenticating$.subscribe((isAuthenticating) => {
      this.loading = isAuthenticating;
    });

    // If the user is authenticated and there is no account & permissions yet fetched then show the loading spinner
    combineLatest([
      this.authService.isAuthenticated,
      this.accountService.selectedAccount,
      this.authService.userPermission,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isAuthenticated, currentAccount, permissions]) => {
        this.loading = isAuthenticated && !currentAccount;
        // If there is an account & permissions fetched call getDefaultEntryPage to set the Webbuy Logo's dynamic link
        if (currentAccount && permissions) {
          this.fallbackView = this.authService.getDefaultEntryPage(currentAccount.uid, permissions);
        }
      });

    let accountUid: string | undefined;
    let userUid: string | undefined;

    // Initialize analytics
    this.analyticService.setSessionUid();

    // Hides/Shows the announcement banner based on the announcement type
    if (localStorage.getItem(`dismissAnnouncement_${this.announcementType}`) === 'true') {
      this.hideAnnouncement = true;
    }

    // Toggles a landscape class on the <body> tag
    // This is used with .container-landscape-override
    // to properly remove the :host-context styling when
    // not in a landscape orientation
    this.displayService.screenOrientation.pipe(takeUntil(this.destroyed$)).subscribe((display: DisplayOrientation) => {
      if (display === DisplayOrientation.PORTRAIT) {
        this.document.body.classList.toggle('landscape', false);
      } else {
        this.document.body.classList.toggle('landscape', true);
      }
    });

    // Set the user data
    this.authContext.userData.pipe(takeUntil(this.destroyed$)).subscribe((userData) => {
      if (!userData) {
        return;
      }

      userUid = this.authContext.getUid();
      if (!userUid) {
        return;
      }

      this.userService.setUserUid(userUid);

      this.analyticService.setUserUid = userUid;
    });

    this.userService.userDetails.pipe(takeUntil(this.destroyed$)).subscribe(async (userDetails: Admin | null) => {
      if (!userDetails) {
        return;
      }
      const isActive = userDetails.status?.trim().toLowerCase() === 'active';

      if (!isActive) {
        this.snackBar.open(this.translate.instant('auth.signIn.deactivationMessage'), '', {
          duration: 3000,
        });

        this.logout(['auth']);
        return;
      }

      if (userDetails.accountUid) {
        this.userDefaultAccount = userDetails.accountUid;
      }

      if (!accountUid) {
        // If no account UID can be found, get the LS accountUid
        accountUid = localStorage.getItem('accountUid') ?? undefined;

        // Set the accountUid to the user's default account
        if (userDetails.accountUid && !accountUid) {
          accountUid = userDetails.accountUid;
        }
      }

      if (userDetails.agree_terms !== '1') {
        new Promise((resolve) => {
          this.dialog
            .open(TermsComponent, {
              autoFocus: true,
              role: 'alertdialog',
              width: '75%',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((closed: any) => {
              resolve(closed);
            });
        }).then((value: any) => {
          if (!value) {
            this.logout(['auth']);
          }
        });
      }

      const { image, first_name: firstName, last_name: lastName } = userDetails;

      this.image = image;
      this.firstName = firstName;
      this.lastName = lastName;
    });

    // Redirects the user appropriately if a redirect query parameter is provided
    this.router.events
      .pipe(
        filter((event: any) => event instanceof NavigationStart && !!event.url),
        take(1),
        takeUntil(this.destroyed$),
      )
      .subscribe((event) => {
        let currentUrl = this.serializer.parse(event.url);
        if (currentUrl.queryParams.r) {
          // Decodes other query parameters from the URL so that redirect links may include them
          let redirectUrl = `/${decodeURIComponent(currentUrl.queryParams.r)}`;
          const urlSegments = currentUrl.root.children.primary?.segments.map((segment) => segment.path);
          if (urlSegments) {
            redirectUrl = `/${urlSegments[0]}` + redirectUrl;
          }
          currentUrl = this.serializer.parse(redirectUrl);

          this.routing.startUrl = currentUrl;
          this.router.navigateByUrl(currentUrl);
        } else {
          this.routing.startUrl = currentUrl;
        }

        // Sets the accountUid if one is set in the URL
        const urlAccountUidSegment = currentUrl.toString().split('/')[2]?.split('?')[0];

        if (
          // New account UIDs
          urlAccountUidSegment?.match(
            '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$',
          ) || // Old account UIDs
          urlAccountUidSegment?.match('^[a-zA-Z0-9]{24}$')
        ) {
          accountUid = urlAccountUidSegment;
          localStorage.setItem('accountUid', accountUid);
        }
      });

    // When the user logs in request and assign the account list
    // Set the selected account based on the provided account
    this.authService.userRoleList
      .pipe(
        takeUntil(this.destroyed$),
        filter((userRoleList) => !!userRoleList),
      )
      .subscribe((permissionAndAccountUid: PermissionAndAccountUid | undefined) => {
        this.userRoles = (permissionAndAccountUid?.permissions as AccountPermission[]) ?? [];
        this.externalRole = this.userRoles.some((role: AccountPermission) => role.access_type === AccessType.EXTERNAL);
        this.authService.setExternalRole(this.externalRole);
        accountUid = permissionAndAccountUid?.accountUid ?? accountUid;

        let accountUids: string[] = [];

        if (!this.externalRole) {
          accountUids = this.userRoles
            ?.filter((role: AccountPermission) => !!role.accountUid)
            .map((role: AccountPermission) => <string>role.accountUid);
        }

        // With the user's permitted accountUids, load the accounts and select the current account
        this.accountService.loadAccounts(accountUids).then(() => {
          if (!accountUid) {
            this.authService.authenticating$.next(false);
            console.error('[error] no accountUid found');
            return;
          }

          if (!this.externalRole && !accountUids.find((one) => one === accountUid)) {
            accountUid = accountUids[0];
          }

          if (!accountUid) {
            this.authService.authenticating$.next(false);
            console.error('[error] no accountUid found');
            return;
          }

          this.accountService.selectAccount(accountUid);

          this.analyticService.setAccountUid = accountUid;
        });
      });

    // Initialize the available account list on user widgets
    this.widgetService.initializeUserWidgetAccounts();

    combineLatest([this.accountService.selectedAccount, this.accountService.getAggregatorAccountEnabled])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([account, accountAggregator]) => {
        if (!account) {
          return;
        }

        this.selectedAccountData = {
          uid: account.uid,
          account: account.account,
          url: account.settings?.find((setting) => setting.key === 'domain')?.value ?? '',
          marketScanId: account.settings?.find((setting) => setting.key === 'marketscan_id')?.value ?? '',
        };
        this.selectedAccountName = account.account;

        if (accountAggregator) {
          this.accountService.loadAggregatorChildAccounts(account.uid);
        }
      });

    this.userService.createUserUidSubscription();

    window.dispatchEvent(new Event('resize'));

    this.updateIsMainMenuOpen(await this.authService.ifSignedInAsync());

    // Configure toggle state
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.route),
        map((route: any) => route.firstChild),
        switchMap((route) => route.data),
        takeUntil(this.destroyed$),
      )
      .subscribe((data: any) => {
        // Detect whether the submenu should be open
        if (data.submenu) {
          this.routeData = data;
          this.isSubmenu = true;
          this.displayService.isSubnavRoute = true;
          this.subMenuService.toggleSubMenuForceOpen();
        } else {
          this.isSubmenu = false;
          this.displayService.isSubnavRoute = false;
          this.subMenuService.toggleSubMenuForceClose();
        }
        this.isSubMenuOpen = this.subMenuService.isSubMenuOpen();

        // Detect whether the main menu should be hidden
        this.updateIsMainMenuOpen(!data.hidemenu);

        this.analyticService.page();
      });

    this.accountService.selectedAccount.pipe(take(1)).subscribe(async (account: Account | undefined) => {
      await this.authService.initIsAuthenticated(account?.account).then(async (defaultAccountUid: string | null) => {
        const urlAccountUid = await this.routing.getSelectedAccountUid(this.route);

        // Use defaultAccount for selectedAccount and permissions unless there is an accountUid in the URL
        if (urlAccountUid || defaultAccountUid) {
          this.accountService.resetAccountData();
          this.accountService.resetSelectedAccountData();
          this.authService.fetchPermissionsByUser(urlAccountUid ?? defaultAccountUid);
          await this.accountService.selectAccount(urlAccountUid ?? defaultAccountUid);
        }
      });
    });

    // Fail-safe for when the route with an account UID is not a valid account
    this.accountService.accountNotFound.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      if (this.userDefaultAccount) {
        this.accountService.selectAccount(this.userDefaultAccount);
      }
    });

    // Get user permissions
    this.authService.userPermission.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (response: PermissionList | undefined) => {
        if (response) {
          this.permission = response;
          this.updateIsMainMenuOpen();
        }
      },
    });

    this.authService.externalRole$.pipe(takeUntil(this.destroyed$)).subscribe((externalAccess: boolean) => {
      this.externalRole = externalAccess;
    });

    this.messageListener = (event: MessageEvent<any>) => {
      this.messageService.pushMessage(event.data);
    };

    window.addEventListener('message', this.messageListener);
  }

  ngAfterViewInit(): void {
    // Set Kiosk mode
    if (this.userService.getKioskModeEnabled) {
      this.setKioskClass();
    }
  }

  ngOnDestroy(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
  }

  get userUid(): string | undefined {
    return this.authService.getUserUid ?? undefined;
  }

  updateIsMainMenuOpen(open?: boolean) {
    // Forces the menu to close if the user is on the deal editor
    if (this.router.url.includes('deal-editor')) {
      this.isMainMenuOpen = false;
      return;
    }

    this.isMainMenuOpen = open !== undefined ? open && !!this.permission : !!this.permission;
  }

  getSupportDetails(): void {
    const supportData = {
      accountName: this.selectedAccountData.account,
      accountUid: this.selectedAccountData.uid,
      accountUrl: this.selectedAccountData.url,
      marketScanId: this.selectedAccountData.marketScanId,
      userUid: this.userUid,
      currentUrl: window.location.href,
    };
    navigator.clipboard.writeText(JSON.stringify(supportData));
    this.snackBar.open(this.translate.instant(`common.copied`), undefined, {
      duration: 3000,
      verticalPosition: 'top',
    });
  }

  logout(route: string[]) {
    this.accountService.resetAccountData();
    this.accountService.resetSelectedAccountData();
    this.authService.logout();
    this.router.navigate([route]);
  }

  addCustomIcons() {
    this.matIconRegistry.addSvgIcon(
      'iconAccountOutline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/account-outline.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconArrowCollapseLeft',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/arrow-collapse-left.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconArrowCollapseRight',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/arrow-collapse-right.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconArrowLeft',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/arrow-left.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconArrowRight',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/arrow-right.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconCarChecked',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/car-checked.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconChevronRight',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/chevron-right.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconCogPlay',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/cog-play.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconCompass',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/compass-2.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconDownload',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/download.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconFacebook',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/facebook.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconFileDocumentOutline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/file-document-outline.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconCar',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/icon-car.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconContract',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/icon-contract.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconCopy',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/icon-copy.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconSold',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/icon-sold.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconKey',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/key.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconPersonOff',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/person-off.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconMicrosoft',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/microsoft.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconFilter',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/filter.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconGoogle',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/google.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconOrganizationNetwork',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/organization-network.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconPencilOutline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/pencil-outline.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconPrint',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/print.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconRestore',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/restore.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconTrashCanOutline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/trash-can-outline.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconUserGroup',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/user-group.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'iconVisibility',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/visibility.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'importData',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/import-data.svg'),
    );
  }

  setupNotifications() {
    // Set Default Environment Notification Message
    this.translate
      .get('auth.signIn.currentEnvironmentMessage', { env: this.feature.getEnv })
      .subscribe((translated: string) => {
        this.notificationMessage = translated;
      });

    // Hide default notification if dismissed or environment is production
    if (localStorage.getItem('dismissNotification') === 'true' || !this.feature.isDev) {
      this.hideNotification = true;
    }

    // Show v1.5 notification if version query param is present
    const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
    if (urlParams.get('version') === '1.5') {
      this.translate.get('auth.signIn.v15message').subscribe((translated: string) => {
        this.notificationMessage = translated;
        this.hideNotification = false;
      });
      // remove version query param from url
      window.history.pushState({}, document.title, `${window.location.origin}${window.location.pathname}`);
    }

    // Hide notification after 60 seconds
    if (!this.hideNotification) {
      setTimeout(() => {
        this.hideNotification = true;
        localStorage.setItem('dismissNotification', 'true');
      }, 60000);
    }
  }

  navigate(dest: string) {
    switch (dest) {
      case 'profile':
        this.router.navigate(['/users', this.accountService.currentUid, this.userUid, 'list-edit'], {
          queryParams: { tab: 0 },
        });
        break;
      case 'notifications':
        this.router.navigate(['/users', this.accountService.currentUid, this.userUid, 'list-edit'], {
          queryParams: { tab: 1 },
        });
        break;
    }
  }

  async setKioskClass(): Promise<void> {
    if (!this.userService.userPin && localStorage.getItem('kioskModeEnabled') !== 'true') {
      const pinSet = await new Promise((resolve) => {
        this.dialog
          .open(PinDialogComponent)
          .afterClosed()
          .pipe(take(1))
          .subscribe((result: boolean) => {
            resolve(result);
          });
      });
      if (!pinSet) {
        return;
      }
    }
    this.renderer.addClass(document.body, 'kiosk-in');
    this.userService.setKioskModeEnabled = true;

    // Destroys the side nav to prevent it appearing in the DOM when in Kiosk Mode
    setTimeout(() => (this.destroySideNav = true), 250);
  }

  unsetKioskClass(): void {
    this.dialog
      .open(PinDialogComponent)
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: any) => {
        if (result) {
          this.destroySideNav = false;
          this.renderer.removeClass(document.body, 'kiosk-in');
          this.userService.setKioskModeEnabled = false;
        }
      });
  }

  get showNotification() {
    return !this.hideNotification;
  }

  get mailtoLink(): string {
    const email = 'support@webbuy.com';
    const subject = this.translate.instant(`announcements.${this.announcementType}.emailSubject`);
    const body = this.translate.instant(`announcements.${this.announcementType}.emailBody`, {
      name: this.userService.getUserFullName,
      account: this.selectedAccountName,
    });
    return `mailto:${email}?subject=${subject}&body=${body}`;
  }

  dismissAnnouncement() {
    this.hideAnnouncement = true;
    localStorage.setItem(`dismissAnnouncement_${this.announcementType}`, 'true');
  }

  dismiss() {
    this.hideNotification = true;
    localStorage.setItem(`dismissAnnouncement_${this.announcementType}`, 'true');
  }

  /* TODO:
      The menus here should have an intermediate state.
      Displaying both the side-nav and the sub-nav,
      in some formats, always crowds the actual component.
      For now I am forcing the side/sub nav menus to only
      stick open if we are in a landscape view format.
  */
  shouldSidenavOpen() {
    return this.displayService.isLandscape;
  }

  isSubnavRoute() {
    if (this.routeData) {
      if (this.routeData.route === 'ACCOUNT_SETTINGS') {
        this.submenuTitle = this.translate.instant('accountSettings.title');
      } else if (this.routeData.route === 'SYSTEM_SETTINGS') {
        this.submenuTitle = this.translate.instant('systemSettings.title');
      }
    }
    return this.isSubmenu;
  }

  private timeoutCurrentUserSession(): void {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
    }
    this.timeoutTimer = setTimeout(() => {
      // If the user is in Kiosk mode then the timer just resets
      if (this.userService.getKioskModeEnabled) {
        this.timeoutCurrentUserSession();
      } else {
        // Reset the user, log them out and send them a message
        this.dialog.closeAll();
        this.logout(['/auth', 'signIn']);
        this.timeoutSnackBarRef = this.snackBar.open(this.translate.instant('auth.signIn.currentUserTimeout'), '', {
          duration: 8000,
        });
      }
    }, 1000 * 60 * this.timeoutLength);
  }
}
